import { useMemo, useState } from "react";
import { formatShortDate } from "../../lib/day.ts";
import type { DailyPoint } from "../../lib/types.ts";

const W = 720;
const H = 260;
const PAD = { top: 16, right: 14, bottom: 28, left: 32 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

const Y_TICKS = [0, 25, 50, 75, 100];

/**
 * Evolucion de la energia.
 *
 * Forma "emphasis": la media movil de 7 dias es el dato que importa y va en el
 * color de acento; la media diaria queda en gris de contexto y la banda
 * min-max, muy tenue, revela los dias volatiles. Un solo eje, una sola medida.
 */
export function EnergyLine({ points }: { points: DailyPoint[] }) {
  const [hover, setHover] = useState<number | null>(null);

  const geometry = useMemo(() => {
    if (points.length === 0) return null;

    const x = (i: number): number =>
      points.length === 1
        ? PAD.left + PLOT_W / 2
        : PAD.left + (i / (points.length - 1)) * PLOT_W;
    const y = (value: number): number => PAD.top + (1 - value / 100) * PLOT_H;

    const line = (getValue: (p: DailyPoint) => number | null): string => {
      let path = "";
      let open = false;
      points.forEach((p, i) => {
        const value = getValue(p);
        if (value === null) {
          open = false;
          return;
        }
        path += `${open ? "L" : "M"}${x(i).toFixed(2)},${y(value).toFixed(2)}`;
        open = true;
      });
      return path;
    };

    const top = points.map((p, i) => `${x(i).toFixed(2)},${y(p.energyMax).toFixed(2)}`);
    const bottom = points
      .map((p, i) => `${x(i).toFixed(2)},${y(p.energyMin).toFixed(2)}`)
      .reverse();

    return {
      x,
      y,
      band: `M${top.join("L")}L${bottom.join("L")}Z`,
      daily: line((p) => p.energyAvg),
      trend: line((p) => p.energyAvg7d),
    };
  }, [points]);

  if (!geometry) {
    return <p className="empty">Aún no hay datos suficientes para la gráfica.</p>;
  }

  const active = hover !== null ? points[hover] : undefined;
  // Solo se marcan los puntos cuando hay pocos: un punto por dia durante un
  // ano seria ruido, no informacion.
  const showDots = points.length <= 45;

  return (
    <figure className="chart">
      <figcaption className="chart__legend">
        <span className="chart__key">
          <span className="chart__swatch chart__swatch--trend" aria-hidden="true" />
          Media de 7 días
        </span>
        <span className="chart__key">
          <span className="chart__swatch chart__swatch--daily" aria-hidden="true" />
          Media del día
        </span>
        <span className="chart__key">
          <span className="chart__swatch chart__swatch--band" aria-hidden="true" />
          Mínimo-máximo del día
        </span>
      </figcaption>

      <div className="chart__plot">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="chart__svg"
          role="img"
          aria-label={`Evolución de la energía en ${points.length} días con registro`}
        >
          {Y_TICKS.map((tick) => {
            const y = geometry.y(tick);
            return (
              <g key={tick}>
                <line
                  x1={PAD.left}
                  x2={W - PAD.right}
                  y1={y}
                  y2={y}
                  className="chart__grid"
                />
                <text x={PAD.left - 7} y={y + 4} className="chart__tick" textAnchor="end">
                  {tick}
                </text>
              </g>
            );
          })}

          <path d={geometry.band} className="chart__band" />
          <path d={geometry.daily} className="chart__line chart__line--daily" />
          <path d={geometry.trend} className="chart__line chart__line--trend" />

          {showDots &&
            points.map((p, i) => (
              <circle
                key={p.date}
                cx={geometry.x(i)}
                cy={geometry.y(p.energyAvg)}
                r={4}
                className="chart__dot"
              />
            ))}

          {active && hover !== null && (
            <g>
              <line
                x1={geometry.x(hover)}
                x2={geometry.x(hover)}
                y1={PAD.top}
                y2={PAD.top + PLOT_H}
                className="chart__crosshair"
              />
              <circle
                cx={geometry.x(hover)}
                cy={geometry.y(active.energyAvg)}
                r={6}
                className="chart__dot chart__dot--active"
              />
            </g>
          )}

          {/* Primera y ultima fecha: suficiente para orientarse sin saturar. */}
          <text x={PAD.left} y={H - 8} className="chart__tick" textAnchor="start">
            {formatShortDate(points[0]!.date)}
          </text>
          <text x={W - PAD.right} y={H - 8} className="chart__tick" textAnchor="end">
            {formatShortDate(points[points.length - 1]!.date)}
          </text>

          <rect
            x={PAD.left}
            y={PAD.top}
            width={PLOT_W}
            height={PLOT_H}
            fill="transparent"
            onPointerMove={(event) => {
              const box = event.currentTarget.getBoundingClientRect();
              const ratio = (event.clientX - box.left) / box.width;
              const index = Math.round(ratio * (points.length - 1));
              setHover(Math.max(0, Math.min(points.length - 1, index)));
            }}
            onPointerLeave={() => setHover(null)}
          />
        </svg>

        {active && hover !== null && (
          <div
            className="chart__tooltip"
            style={{
              left: `${(geometry.x(hover) / W) * 100}%`,
              transform: `translateX(${hover > points.length / 2 ? "-100%" : "0"}) translateX(${hover > points.length / 2 ? "-10px" : "10px"})`,
            }}
          >
            <strong>{formatShortDate(active.date)}</strong>
            <span>
              {active.mood?.emoji} Media {active.energyAvg}
            </span>
            <span className="subtle">
              {active.count > 1
                ? `${active.count} registros · ${active.energyMin}-${active.energyMax}`
                : "1 registro"}
            </span>
            {active.energyAvg7d !== null && (
              <span className="subtle">7 días: {active.energyAvg7d}</span>
            )}
          </div>
        )}
      </div>
    </figure>
  );
}
